import React from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { FetchAPI, FieldAPI, FormAPI, InputAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ReactComponent as MagnifierIcon } from '../../../resources/assets/search.svg';
import { ProviderAccreditationEmployeesAliases as Aliases } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './provider-accreditation-controller';

interface Employee {
  personName: string;
  cdTaxpayerId: string;
  dsEmail: string;
  phone: string;
  nrSequence: number;
  nrSeqTypeOfLink: number;
  dsTypeOfLink: string;
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: 'provider-accreditation-employees' })
export default class ProviderAccreditationEmployees extends BaseController {

  @Inject(Aliases.FETCH_LIST_EMPLOYEES)
  private fetchListEmployees: FetchAPI;
  @Inject(Aliases.FETCH_EMPLOYEE)
  private fetchEmployee: FetchAPI;
  @Inject(Aliases.FORM_EMPLOYEE)
  private formEmployee!: FormAPI;
  @Inject(Aliases.FIELD_NAME)
  private fieldName: FieldAPI;
  @Inject(Aliases.FIELD_TYPE_OF_LINK)
  private fieldTypeOfLink: FieldAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.INPUT_FILTER)
  private inputFilter: InputAPI;

  @OnMount(Aliases.LAYOUT_EMPLOYEES)
  onMountLayout(): void {
    const { nrSeqUser, nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqUser, nrSeqAccreditation, nrSeqTypeAccreditation });
    this.fetchListEmployees.forceFetch();
  }

  @OnCancel(Aliases.FORM_EMPLOYEE)
  onCancelForm(): void {
    this.cleanForm();
  }

  @OnChange(Aliases.FORM_EMPLOYEE)
  onChangeForm(): void {
    this.formEmployee.setState({ forceValidate: true });
  }

  @OnSubmit(Aliases.FORM_EMPLOYEE)
  onSubmitFormEmployee(event: FormAPI): void {
    const body = event.record;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.formEmployee.setState({ loading: true });
    void HttpService.post(Endpoint.POST.EMPLOYEE, { nrSeqUser, nrSeqAccreditation }, { body })
      .then(() => {
        this.cleanForm();
        this.formEmployee.setState({ loading: false });
        this.fetchListEmployees.forceFetch();
      }).catch((error) => {
        this.formEmployee.setState({ loading: false });
        ToastServiceProviderAccreditation.error(error.body);
      });
  }

  private cleanForm(): void {
    this.formEmployee.setRecord({});
    this.formEmployee.setData({});
    this.fieldName.setState({ valid: true, showValidations: false });
    this.fieldTypeOfLink.setState({ showValidations: false });
  }

  @OnMount(Aliases.PANEL_CONTENT_FORM_EMPLOYEE)
  onMountForm(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.EMPLOYEES);
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetch(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.EMPLOYEES);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  @OnMount(Aliases.INPUT_FILTER)
  onMountFilter(): void {
    this.inputFilter.setExternalAction(<MagnifierIcon className="c-module-selector__search-icon" />);
  }

  @OnChange(Aliases.INPUT_FILTER)
  onEditFilter(event: ChangeEvent<any>): void {
    const params = this.fetchListEmployees.getParameters();
    const desc = event.value;
    this.fetchListEmployees.setParameters({ ...params, desc });
    this.fetchListEmployees.forceFetch();
  }

  @HPMSOnCreateMenu(Aliases.HPMS_EMPLOYEE_CARD)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @HPMSOnOptionClick(Aliases.HPMS_EMPLOYEE_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Employee>): void {
    const { data, key } = event;
    const { nrSequence } = data;
    const action = {
      [OptionKey.EDIT]: () => this.editEmployee(nrSequence),
      [OptionKey.DELETE]: () => this.deleteEmployee(nrSequence),
    };
    if (action[key]) {
      action[key]();
    }
  }

  private editEmployee(nrSequence: number): void {
    this.cleanForm();
    this.fetchEmployee.setParameters({ nrSequence });
    this.fetchEmployee.forceFetch();
  }

  private deleteEmployee(nrSequence: number): void {
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    HttpService.delete(Endpoint.DELETE.EMPLOYEE, { nrSeqUser, nrSequence, nrSeqAccreditation })
      .then(() => {
        this.cleanForm();
        this.fetchListEmployees.forceFetch();
      }).catch(() => {
        ToastServiceProviderAccreditation.error('pa.error-process-request');
        this.fetchListEmployees.forceFetch();
      });
  }
}
